import { DashboardLayout } from "components/DashboardLayout";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { RegistrationOnboarding } from "components/StateRegistrations/RegistrationOnboarding";
import { StateComplianceDashboard } from "components/StateRegistrations/StateComplianceDashboard";
import ToolTip from "components/design/toolTip";
import { BookBookmark } from "components/icons/BookBookmark";
import { CheckOutline } from "components/icons/CheckOutline";
import { Document } from "components/icons/Document";
import { Mail } from "components/icons/Mail";
import { MapPin } from "components/icons/MapPin";
import { Refresh } from "components/icons/Refresh";
import { UsersThree } from "components/icons/UsersThree";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import React, { useState } from "react";
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import HomeStateIcon from "static/images/HomeStateIcon.svg";
import {
  StateRegistrationName,
  StateRegistrationNameType,
  StateRegistrationsRes,
  useGetStateRegistrationsQuery,
  useSubmitRegistrationIntakeMutation,
  useUpdateStateRegistrationMutation,
} from "store/apis/StateRegistrations";
import { useCreateMessageDraftMutation } from "store/apis/chat";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import { useGetRegisteredAgentQuery } from "store/apis/registerAgent";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import {
  isFqDateAttr,
  isSosNumberAttr,
  MISSING_FQ_DATE_LABEL,
  MISSING_SOS_NUMBER_LABEL,
} from "components/StateRegistrations/constants";

type ServiceItem = {
  name: string;
  description: string;
  icon: React.ReactNode;
  chatMessage?: string;
  registrationType?: StateRegistrationNameType;
};

type ServiceGroup = {
  label: string;
  items: ServiceItem[];
};

const SERVICE_GROUPS: ServiceGroup[] = [
  {
    label: "Register & set up",
    items: [
      {
        name: "Foreign Qualification",
        description:
          "Register your entity to legally operate, hire, or conduct business in a state outside your home state",
        icon: <MapPin size="14" />,
        registrationType: StateRegistrationName.SECRETARY_OF_STATE,
      },
      {
        name: "Registered Agent",
        description:
          "Appoint Inkle as your registered agent to receive all official state and legal correspondence on your behalf.",
        icon: <Mail />,
        registrationType: StateRegistrationName.REGISTERED_AGENT,
      },
      {
        name: "Payroll Tax Registration",
        description:
          "Set up state withholding and unemployment insurance accounts before running your first in-state payroll.",
        icon: <UsersThree />,
        registrationType: StateRegistrationName.PAY_ROLL,
      },
    ],
  },
  {
    label: "Prove standing",
    items: [
      {
        name: "Good Standing Certificate",
        description:
          "Obtain an official certificate confirming your entity is active and in compliance with state requirements.",
        icon: (
          <span>
            <CheckOutline width={"5"} height={"5"} />
          </span>
        ),
        chatMessage:
          "Hi, I would like to request a Good Standing Certificate for our entity in {state}. Please let us know what information you need to get started.",
      },
    ],
  },
  {
    label: "Fix & resolve",
    items: [
      {
        name: "Reinstatement / Revival",
        description:
          "Restore an entity that has been suspended or administratively dissolved back to active good standing.",
        icon: <Refresh size="14" />,
        chatMessage:
          "Hi, our entity has been suspended or dissolved in {state} and we would like to restore it to active good standing. Please advise on the reinstatement or revival process and the next steps required.",
      },
      {
        name: "Amendment",
        description:
          "File a formal update to change your registered address, registered agent, officers, directors, or other entity details.",
        icon: <Document />,
        chatMessage:
          "Hi, we would like to file an amendment to update our entity details for {entity}, which includes changes to our authorized shares, or other registered information. Please let us know how to proceed.",
      },
    ],
  },
  {
    label: "Wind down",
    items: [
      {
        name: "Withdrawal / Dissolution",
        description:
          "Formally withdraw your entity from a state or dissolve it entirely, closing all outstanding obligations.",
        icon: <BookBookmark />,
        chatMessage:
          "Hi, we would like to formally withdraw our entity from {state} or proceed with a formal company dissolution to close all outstanding obligations. Please guide us through the required steps.",
      },
    ],
  },
];

const DONE_REG_STATUSES = new Set(["REGISTERED", "NOT_APPLICABLE"]);

const OrderServiceModal = ({
  isOpen,
  close,
  stateName,
  entityName,
  stateDetail,
  isHomeState,
  onRegisterNow,
}: {
  isOpen: boolean;
  close: () => void;
  stateName: string;
  entityName: string;
  stateDetail: StateRegistrationsRes | null;
  isHomeState: boolean;
  onRegisterNow: (type: StateRegistrationNameType) => void;
}) => {
  const { secondary_channel_url: generalHelpChannelUrl } =
    useCurrentGroupContext();
  const history = useHistory();
  const [createUpdateDraft] = useCreateMessageDraftMutation();

  const handleItemClick = async (item: ServiceItem) => {
    if (item.registrationType) {
      close();
      onRegisterNow(item.registrationType);
      return;
    }
    if (!item.chatMessage || !generalHelpChannelUrl) return;
    close();
    const message = item.chatMessage
      .replace(/\{state\}/g, stateName)
      .replace(/\{entity\}/g, entityName);
    await createUpdateDraft({
      channelUrl: generalHelpChannelUrl,
      message,
    });
    history.push(`/chat/${generalHelpChannelUrl}`);
  };

  const HOME_STATE_HIDDEN_TYPES = new Set<string>([
    StateRegistrationName.SECRETARY_OF_STATE,
    StateRegistrationName.REGISTERED_AGENT,
  ]);

  const isRegDone = (item: ServiceItem) => {
    if (!item.registrationType || !stateDetail) return false;
    if (isHomeState && HOME_STATE_HIDDEN_TYPES.has(item.registrationType))
      return true;
    const reg = stateDetail[item.registrationType];
    const statusUpper = reg?.status?.toUpperCase() ?? "";
    return DONE_REG_STATUSES.has(statusUpper);
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large">
        <Modal.Header className="sm:!t-items-start sm:!t-px-4">
          <div>
            <Modal.Title>What do you need?</Modal.Title>
            <p className="t-m-0 t-text-body-sm t-text-text-30">
              Select a service to get started.
            </p>
          </div>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-5 sm:!t-px-4">
          {SERVICE_GROUPS.map((group) => {
            const visibleItems = group.items.filter((item) => !isRegDone(item));
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.label} className="t-flex t-flex-col t-gap-2">
                <p className="t-m-0 t-text-[10px] t-font-semibold t-uppercase t-tracking-wider t-text-text-30">
                  {group.label}
                </p>
                <div className="t-grid t-grid-cols-2 t-gap-2 sm:t-grid-cols-1">
                  {visibleItems.map((item) => (
                    <div
                      key={item.name}
                      onClick={() => void handleItemClick(item)}
                      className="t-flex t-flex-col t-gap-1 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-3 t-cursor-pointer hover:t-bg-surface-lighter-grey"
                    >
                      <div className="t-flex t-items-center t-gap-2">
                        <div className="t-flex t-h-7 t-w-7 t-shrink-0 t-items-center t-justify-center t-rounded-full t-bg-purple-0 t-text-purple [&_svg]:t-size-4">
                          {item.icon}
                        </div>
                        <span className="t-text-body-sm t-font-semibold t-text-text-100">
                          {item.name}
                        </span>
                      </div>
                      <p className="t-m-0 t-text-body-sm t-text-text-30">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export const StateRegistrationDetail = () => {
  const { stateId } = useParams<{ stateId: string }>();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const query = useQuery();
  const entityId = query.get("entity");
  const entityName = entities?.find((e) => e.uuid === entityId)?.name ?? "";
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { isAdmin } = useRoleBasedView();
  const registrationsListUrl = url.replace(`/${stateId}`, "");

  const orderServiceModal = useModal();
  const [rowOnboardingType, setRowOnboardingType] =
    useState<StateRegistrationNameType | null>(null);
  const { alertToast, successToast } = useToast();
  const [updateRegistration] = useUpdateStateRegistrationMutation();
  const [submitIntake, { isLoading: isRenewLoading }] =
    useSubmitRegistrationIntakeMutation();

  const {
    data: stateRegistrations = [],
    isLoading,
    isSuccess: stateRegistrationsLoaded,
  } = useGetStateRegistrationsQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !entityId || !groupId }
  );

  const { data: registeredAgents = [] } = useGetRegisteredAgentQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !groupId || !entityId }
  );

  const { data: entityAttributes = [], isLoading: isLoadingAttributes } =
    useGetEntityDataAttributesQuery(
      { groupId: groupId!, entityId: entityId! },
      { skip: !groupId || !entityId || !stateRegistrationsLoaded }
    );

  const stateDetail = stateRegistrations.find((s) => s.state_id === stateId);
  const stateName = stateDetail?.state || "";
  const isHomeState = stateDetail?.is_home_state || false;

  usePageTitle(stateName ? `${stateName} Compliance` : "Compliance");

  if (isLoading || isLoadingAttributes) {
    return <Loader />;
  }

  const missingLabels = new Set(
    stateDetail?.compliance_status.missing_field_labels ?? []
  );
  const missingFields = entityAttributes.filter(
    (attr) => !attr.value && missingLabels.has(attr.label ?? attr.name)
  );

  const sosDataAttrs = stateDetail?.SECRETARY_OF_STATE?.data_attributes ?? [];

  if (missingLabels.has(MISSING_FQ_DATE_LABEL)) {
    const fqDateAttr = sosDataAttrs.find((a) => isFqDateAttr(a) && !a.value);
    if (fqDateAttr) {
      missingFields.push({
        name: fqDateAttr.field_key,
        label: MISSING_FQ_DATE_LABEL,
        value: null,
        isStateField: true,
      } as any);
    }
  }

  if (missingLabels.has(MISSING_SOS_NUMBER_LABEL)) {
    const sosNumberAttr = sosDataAttrs.find(
      (a) => isSosNumberAttr(a) && !a.value
    );
    if (sosNumberAttr) {
      missingFields.push({
        name: sosNumberAttr.field_key,
        label: MISSING_SOS_NUMBER_LABEL,
        value: null,
        isStateField: true,
      } as any);
    }
  }

  const handleRenewRA = async () => {
    if (!groupId || !entityId || !stateId) return;
    try {
      await submitIntake({
        groupId,
        entityId,
        stateId,
        intakeEntries: [
          { type: StateRegistrationName.REGISTERED_AGENT, fields: {} },
        ],
      }).unwrap();
      successToast({
        message: `RA for ${stateName} added to cart successfully!`,
      });
      const cartUrl = isAdmin
        ? `/admin/crm/${groupId}/billing/cart?entity=${entityId}`
        : `/settings/billing/cart?entity=${entityId}&settings_back_to=${encodeURIComponent(
            location.pathname + location.search
          )}`;
      openLink(cartUrl, "_blank");
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const openRowOnboarding = (type: StateRegistrationNameType) => {
    if (type === StateRegistrationName.REGISTERED_AGENT) {
      void handleRenewRA();
      return;
    }
    setRowOnboardingType(type);
  };

  const closeRowOnboarding = () => {
    setRowOnboardingType(null);
  };

  const titleNode = stateName ? (
    <div className="t-flex t-items-center t-gap-2">
      <span>{stateName}</span>
      {isHomeState && (
        <ToolTip text="State of Incorporation">
          <img src={HomeStateIcon} alt="HomeStateIcon" />
        </ToolTip>
      )}
      {stateDetail?.withdrawal_date && (
        <Tag tagType="red" rounded size="small" transparent>
          {`Withdrawn ${dayjs(stateDetail.withdrawal_date).format(
            DISPLAY_DATE
          )}`}
        </Tag>
      )}
    </div>
  ) : null;

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title={titleNode}
          breadcrumbs={[
            { name: "State Compliance", link: registrationsListUrl },
            { name: stateName },
          ]}
          right={
            <Button
              customType="secondary"
              size="small"
              onClick={orderServiceModal.open}
            >
              Order a service
            </Button>
          }
        />
      }
    >
      <div className="t-mt-4 t-flex t-flex-col t-gap-4">
        {stateDetail && (
          <StateComplianceDashboard
            stateDetail={stateDetail}
            entityAttributes={entityAttributes}
            entityId={entityId!}
            entityName={entityName}
            stateId={stateId!}
            missingFields={missingFields}
            defaultOpenMissingInfo={query.get("openMissingInfo") === "true"}
            onRegisterNow={openRowOnboarding}
            isRenewLoading={isRenewLoading}
          />
        )}
      </div>

      {stateDetail && rowOnboardingType && (
        <RegistrationOnboarding
          open={Boolean(rowOnboardingType)}
          onClose={closeRowOnboarding}
          entryPoint="row"
          stateData={stateDetail}
          rowConfig={{
            type: rowOnboardingType,
            stateName,
            stateId,
          }}
        />
      )}

      <OrderServiceModal
        isOpen={orderServiceModal.isOpen}
        close={orderServiceModal.close}
        stateName={stateName}
        entityName={entityName}
        stateDetail={stateDetail ?? null}
        isHomeState={isHomeState}
        onRegisterNow={openRowOnboarding}
      />
    </DashboardLayout>
  );
};
